/**
 * What an answer hands you to decide on, as a card under it: a worker's plan (or a change to one), a
 * single order, a group of plans, a watcher or a scan. Each shows what it comes to in a few rows and
 * waits for you; the whole of it opens on its own page.
 */
import type { ReactNode } from 'react'
import { BROKER_LABEL } from '@shared/brokers'
import { isScan } from '@shared/watch'
import type { WorkerRecord } from '@shared/worker'
import { useOrder } from '../hooks/useOrder'
import { useWatcher } from '../hooks/useWatcher'
import { useWorker, useWorkerGroup } from '../hooks/useWorker'
import { ordersApi, watchersApi, workersApi } from './api'
import { Btn, Card, Status, askSure, cx, usd } from './kit'
import { qtyText, useTabs } from './Page'
import { useDemo } from './store'
import { entryText, workerState } from './Work'

const say = (m: string) => useDemo.getState().say(m)
const fail = (e: unknown) => say(e instanceof Error ? e.message : 'That did not work')
const money = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${usd(Math.abs(n))}`
const acct = (broker: string | undefined, account: string | null) => `${BROKER_LABEL[(broker ?? 'ibkr') as keyof typeof BROKER_LABEL]}${account ? ` ··${account.slice(-4)}` : ''}`
const openWorker = (id: string) => useDemo.setState({ view: 'workers', workerId: id, navOpen: false })
const openTab = (tab: string) => (useDemo.getState().go('workers'), useTabs.getState().set({ workers: tab }))

const Shell = ({ top, state, children }: { top: ReactNode; state: ReactNode; children: ReactNode }) => (
  <Card className="w-full max-w-[440px] px-3 pb-3 pt-[10px]">
    <div className="flex h-5 items-center text-[12px] tracking-normal text-ws-fg-3">
      <span className="min-w-0 truncate">{top}</span>
      <span className="ml-auto pl-2">{state}</span>
    </div>
    {children}
  </Card>
)
const Rows = ({ children }: { children: ReactNode }) => <dl className="m-0 mt-2 border-t border-ws-edge pt-2 text-[12px] tracking-normal">{children}</dl>
const Line = ({ k, children, cls = 'text-ws-fg-2' }: { k: string; children: ReactNode; cls?: string }) => (
  <div className="flex min-h-6 items-center justify-between gap-3">
    <dt className="shrink-0 text-ws-fg-3">{k}</dt>
    <dd className={cx('m-0 min-w-0 truncate text-right tabular-nums', cls)}>{children}</dd>
  </div>
)
const Warnings = ({ list }: { list: string[] }) =>
  list.length ? (
    <ul className="m-0 mt-2 list-none p-0">
      {list.map((x) => (
        <li key={x} className="flex gap-2 py-[3px] text-[12px] leading-[17px] tracking-normal text-ws-fg-2">
          <span className="mt-[6px] block h-[6px] w-[6px] shrink-0 rounded-full bg-ws-warn" />
          {x}
        </li>
      ))}
    </ul>
  ) : null
const Note = ({ children }: { children: ReactNode }) => <p className="m-0 mt-2 line-clamp-4 text-[12px] leading-[17px] tracking-normal text-ws-fg-3">{children}</p>
const Waiting = () => <div className="h-[132px] w-full max-w-[440px] animate-ws-pulse rounded-[10px] bg-ws-panel" />
const blocked = (checks: { ok: boolean; level?: string; label: string; detail: string }[]) => checks.filter((c) => !c.ok && c.level !== 'info').map((c) => `${c.label} — ${c.detail}`)

/** A worker's plan — or a change to a running one — waiting for you, and what became of it afterwards. */
export function PlanCard({ id }: { id: string }) {
  const { worker: w, error } = useWorker(id)
  if (!w) return error ? <Note>{error}</Note> : <Waiting />
  const draft = w.status === 'draft'
  const change = w.summary.revision
  const s = w.summary
  const stops = [...new Set(w.plan.lots.filter((l) => !l.noStop).map((l) => l.sl.stop))]
  const targets = w.plan.lots.map((l) => l.tp?.price).filter((v): v is number => v != null)
  const stop = blocked(w.checks)
  const state = draft ? { tone: 'warn' as const, label: 'Waiting for you' } : w.status === 'applied' ? { tone: 'free' as const, label: 'Approved' } : workerState(w as WorkerRecord)
  const target = change ? change.of : w.id
  return (
    <Shell top={acct(w.spec.broker, w.account)} state={<Status tone={state.tone}>{state.label}</Status>}>
      <div className="mt-[6px] font-medium leading-5 text-ws-fg">
        {change ? (
          <>Change to {change.ofName}</>
        ) : w.spec.adopt ? (
          <>
            Manage {qtyText(w.plan.quantity)} {w.symbol} <span className="font-normal text-ws-fg-3">· already held · {w.plan.lots.length} {w.plan.lots.length === 1 ? 'lot' : 'lots'}</span>
          </>
        ) : (
          <>
            <span className="text-ws-up">BUY</span> {qtyText(w.plan.quantity)} {w.symbol}
            <span className="font-normal text-ws-fg-3">
              {' '}
              · {entryText(w)} · {w.plan.lots.length} {w.plan.lots.length === 1 ? 'lot' : 'lots'}
            </span>
          </>
        )}
      </div>
      <div className="mt-px truncate text-[12px] leading-4 tracking-normal text-ws-fg-3">{w.name}</div>
      {change ? (
        <ul className="m-0 mt-2 list-none divide-y divide-ws-edge border-y border-ws-edge p-0">
          {change.lines.map((x) => (
            <li key={x} className="py-[6px] text-[12px] leading-[17px] tracking-normal tabular-nums text-ws-fg-2">
              {x}
            </li>
          ))}
        </ul>
      ) : (
        <Rows>
          <Line k={targets.length > 1 ? 'Targets' : 'Target'}>{targets.length ? [...new Set(targets)].map((t) => t.toFixed(2)).join(' · ') : 'rides with its stop'}</Line>
          <Line k={stops.length > 1 ? 'Stops' : 'Stop'}>{stops.length ? stops.map((t) => t.toFixed(2)).join(' · ') : 'none'}</Line>
          <Line k="Costs about">{usd(s.notional)}</Line>
          <Line k="Most it can lose" cls="text-ws-down">
            {money(-(s.maxLossNet ?? s.maxLoss))}
          </Line>
          {(s.bestCaseNet ?? s.bestCase) != null && (
            <Line k="If every target fills" cls="text-ws-up">
              {money((s.bestCaseNet ?? s.bestCase)!)}
            </Line>
          )}
        </Rows>
      )}
      <Warnings list={[...stop, ...(change?.warnings ?? []), ...(draft ? s.warnings.slice(0, 2) : [])]} />
      {w.spec.note && !change && <Note>{w.spec.note}</Note>}
      {w.error && <Note>{w.error}</Note>}
      <div className="mt-3 flex h-8 items-center gap-2">
        {draft && (
          <>
            <Btn variant="white" disabled={stop.length > 0} title={stop.length ? 'A risk check stands in the way' : 'Approval is bound to exactly this plan'} onClick={() => void workersApi.approve(w.id, w.planHash).then(() => say(change ? 'Change approved — the worker runs the new plan' : 'Approved — the worker takes it from here'), fail)}>
              Approve
            </Btn>
            <Btn variant="outline" onPanel onClick={() => void workersApi.reject(w.id).catch(fail)}>
              Reject
            </Btn>
          </>
        )}
        <button type="button" onClick={() => openWorker(target)} className={cx('rounded-[6px] px-2 py-1 text-[12px] tracking-normal text-ws-fg-3 transition-colors duration-150 hover:bg-ws-raise hover:text-ws-fg', draft && 'ml-auto')}>
          {change ? 'Open the worker' : 'Open the whole plan'}
        </button>
      </div>
    </Shell>
  )
}

const TICKET: Record<string, { tone: 'free' | 'warn' | 'off' | 'down'; label: string }> = { draft: { tone: 'warn', label: 'Waiting for you' }, working: { tone: 'free', label: 'Sent' }, filled: { tone: 'free', label: 'Filled' }, partial: { tone: 'warn', label: 'Partly filled' }, cancelled: { tone: 'off', label: 'Cancelled' }, rejected: { tone: 'off', label: 'Rejected' }, expired: { tone: 'off', label: 'Expired' }, error: { tone: 'down', label: 'Not sent' } }
const KIND: Record<string, string> = { MKT: 'market', LMT: 'limit', STP: 'stop', 'STP LMT': 'stop limit', TRAIL: 'trailing stop', 'TRAIL LIMIT': 'trailing stop limit', MIT: 'market if touched', LIT: 'limit if touched', MOC: 'market on close', LOC: 'limit on close' }
const TIF: Record<string, string> = { DAY: 'day', GTC: 'until cancelled', GTD: 'until a date', IOC: 'now or never', OPG: 'at the open' }

/** One order drafted in a conversation. */
export function TicketCard({ id }: { id: string }) {
  const { ticket: t, error } = useOrder(id)
  if (!t) return error ? <Note>{error}</Note> : <Waiting />
  const sp = t.spec
  const stop = blocked(t.checks)
  const filled = t.fills.filter((f) => f.key === 'O').reduce((a, f) => a + f.shares, 0)
  const avg = filled ? t.fills.filter((f) => f.key === 'O').reduce((a, f) => a + f.shares * f.price, 0) / filled : 0
  const price = sp.limitPrice ?? sp.stopPrice ?? sp.triggerPrice
  const state = TICKET[t.status] ?? { tone: 'off' as const, label: t.status }
  return (
    <Shell top={acct(sp.broker, t.account)} state={<Status tone={state.tone}>{state.label}</Status>}>
      <div className="mt-[6px] font-medium leading-5 text-ws-fg">
        <span className={sp.action === 'SELL' ? 'text-ws-down' : 'text-ws-up'}>{sp.action}</span> {t.summary.quantityText} {t.symbol}
        <span className="font-normal text-ws-fg-3">
          {' '}
          · {KIND[sp.orderType] ?? sp.orderType}
          {price != null ? ` ${price.toFixed(2)}` : ''} · {TIF[sp.tif] ?? sp.tif}
        </span>
      </div>
      <Rows>
        <Line k="Estimated value">{usd(t.summary.notional)}</Line>
        <Line k="Position">{t.summary.positionText}</Line>
        {sp.bracket?.takeProfit != null && <Line k="Target">{sp.bracket.takeProfit.toFixed(2)}</Line>}
        {sp.bracket?.stopLoss != null && <Line k="Stop">{sp.bracket.stopLoss.toFixed(2)}</Line>}
        {(t.summary.maxLossNet ?? t.summary.maxLoss) != null && (
          <Line k="Most it can lose" cls="text-ws-down">
            {money(-(t.summary.maxLossNet ?? t.summary.maxLoss)!)}
          </Line>
        )}
        <Line k="Commission">{t.summary.fees ? usd(t.summary.fees.order) : t.preview?.commission != null ? usd(t.preview.commission) : '$0.00'}</Line>
        {filled > 0 && (
          <Line k={sp.action === 'BUY' ? 'Bought' : 'Sold'}>
            {qtyText(filled)} at {avg.toFixed(2)}
          </Line>
        )}
      </Rows>
      <Warnings list={[...stop, ...(t.status === 'draft' ? t.summary.warnings.slice(0, 2) : [])]} />
      {sp.note && <Note>{sp.note}</Note>}
      {t.error && <Note>{t.error}</Note>}
      {(t.status === 'draft' || t.status === 'working') && (
        <div className="mt-3 flex h-8 items-center gap-2">
          {t.status === 'draft' ? (
            <>
              <Btn variant="white" disabled={stop.length > 0} title={stop.length ? 'A check stands in the way' : 'Exactly this order is sent'} onClick={() => void ordersApi.approve(t.id, t.planHash).then(() => say('Approved — the order is on its way'), fail)}>
                Approve
              </Btn>
              <Btn variant="outline" onPanel onClick={() => void ordersApi.reject(t.id).catch(fail)}>
                Reject
              </Btn>
            </>
          ) : (
            <Btn variant="outline" onPanel onClick={() => askSure({ title: 'Cancel this order?', body: 'What has not filled is taken back from the broker.', cta: 'Cancel order', done: () => void ordersApi.cancel(t.id).then((r) => r.error && say(r.error), fail) })}>
              Cancel order
            </Btn>
          )}
        </div>
      )}
    </Shell>
  )
}

/** Several plans approved together. */
export function GroupCard({ id }: { id: string }) {
  const { group: g, error } = useWorkerGroup(id)
  if (!g) return error ? <Note>{error}</Note> : <Waiting />
  const draft = g.status === 'draft'
  const state = draft ? { tone: 'warn' as const, label: 'Waiting for you' } : g.status === 'active' ? { tone: 'free' as const, label: 'Running' } : g.status === 'error' ? { tone: 'down' as const, label: 'Needs you' } : { tone: 'off' as const, label: g.status === 'done' ? 'Finished' : g.status === 'rejected' ? 'Rejected' : g.status === 'expired' ? 'Expired' : 'Stopped' }
  return (
    <Shell top={`${g.summary.members.length} plans together`} state={<Status tone={state.tone}>{state.label}</Status>}>
      <div className="mt-[6px] font-medium leading-5 text-ws-fg">{g.name}</div>
      <div className="mt-2 divide-y divide-ws-edge border-y border-ws-edge">
        {g.summary.members.map((m) => (
          <button key={m.key} type="button" onClick={() => openWorker(m.workerId)} className="flex min-h-9 w-full items-center gap-3 py-[5px] text-left text-[12px] tracking-normal transition-colors duration-150 hover:text-ws-fg">
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] text-ws-fg-2">{m.name}</span>
              <span className="block truncate text-ws-fg-3">{m.startText ?? m.headline}</span>
            </span>
            <span className="shrink-0 tabular-nums text-ws-down">{money(-m.maxLoss)}</span>
          </button>
        ))}
      </div>
      <Rows>
        <Line k={g.spec.oneCancelsOthers ? 'Most it can lose · only one is bought' : 'Most it can lose · all of them'} cls="text-ws-down">
          {money(-g.summary.maxLoss)}
        </Line>
      </Rows>
      <Warnings list={[...g.summary.warnings.slice(0, 3), ...(g.error ? [g.error] : [])]} />
      {g.summary.behaviour[0] && <Note>{g.summary.behaviour.join(' ')}</Note>}
      <div className="mt-3 flex h-8 items-center gap-2">
        {draft && (
          <>
            <Btn variant="white" title="Approval is bound to exactly these plans" onClick={() => void workersApi.approveGroup(g.id, g.planHash).then(() => say('Approved — the group is running'), fail)}>
              Approve
            </Btn>
            <Btn variant="outline" onPanel onClick={() => void workersApi.rejectGroup(g.id).catch(fail)}>
              Reject
            </Btn>
          </>
        )}
        <button type="button" onClick={() => openTab('groups')} className={cx('rounded-[6px] px-2 py-1 text-[12px] tracking-normal text-ws-fg-3 transition-colors duration-150 hover:bg-ws-raise hover:text-ws-fg', draft && 'ml-auto')}>
          Open the group
        </button>
      </div>
    </Shell>
  )
}

/** Something an agent set up to keep an eye on: a watcher, or a scan of the market. */
export function WatchCard({ id }: { id: string }) {
  const { watcher: w, error } = useWatcher(id)
  if (!w) return error ? <Note>{error}</Note> : <Waiting />
  const scan = isScan(w.spec)
  const hits = w.runtime.lastHits ?? []
  return (
    <Shell top={scan ? 'Scan' : `Watcher · ${(w.spec as { symbol: string }).symbol}`} state={<Status tone={w.status === 'active' ? 'free' : 'off'}>{w.status === 'active' ? (w.checkingNow ? 'Looking now' : 'Active') : w.status === 'paused' ? 'Paused' : 'Ended'}</Status>}>
      <div className="mt-[6px] font-medium leading-5 text-ws-fg">{w.name}</div>
      <div className="mt-px text-[12px] leading-4 tracking-normal text-ws-fg-3">{w.scheduleText ?? w.hoursText}</div>
      {w.rules.length > 0 && (
        <ul className="m-0 mt-2 list-none divide-y divide-ws-edge border-y border-ws-edge p-0">
          {w.rules.map((r) => (
            <li key={r.id} className="flex gap-2 py-[6px] text-[12px] leading-[17px] tracking-normal">
              <span className={cx('mt-[6px] block h-[6px] w-[6px] shrink-0 rounded-full', r.done ? 'bg-ws-fg-5' : r.holds ? 'bg-ws-up' : 'bg-ws-fg-4')} />
              <span className="min-w-0">
                <span className="block text-ws-fg-2">{r.text}</span>
                {r.now && <span className="block text-ws-fg-4">{r.now}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
      {hits.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {hits.slice(0, 12).map((h) => (
            <button key={h.symbol} type="button" onClick={() => useDemo.getState().openStock(h.symbol)} className="flex h-6 items-center rounded-[6px] bg-ws-raise px-2 text-[12px] font-medium tracking-normal text-ws-fg-2 transition-colors duration-150 hover:text-ws-fg">
              {h.symbol}
            </button>
          ))}
        </div>
      )}
      <div className="mt-3 flex h-8 items-center gap-2">
        {w.status !== 'done' && (
          <Btn variant="outline" onPanel onClick={() => void watchersApi.setStatus(w.id, w.status === 'paused' ? 'active' : 'paused').catch(fail)}>
            {w.status === 'paused' ? 'Resume' : 'Pause'}
          </Btn>
        )}
        <button type="button" onClick={() => openTab('watchers')} className="ml-auto rounded-[6px] px-2 py-1 text-[12px] tracking-normal text-ws-fg-3 transition-colors duration-150 hover:bg-ws-raise hover:text-ws-fg">
          All watchers and scans
        </button>
      </div>
    </Shell>
  )
}
